import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useLocation } from "wouter";
import {
  Eye,
  Presentation,
  Moon,
  Sun,
  Maximize,
  Minimize,
  FileText,
  BookOpen,
  Menu,
  BookText,
  MoreHorizontal,
  AlertCircle,
  FileDown,
  History,
  Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sidebar } from "@/components/layout/Sidebar";
import { DocumentSidebar } from "@/components/layout/DocumentSidebar";
import { DocumentTitle } from "@/components/editor/DocumentTitle";
import { EditorMenus } from "@/components/editor/EditorMenus";
import { ExportMenu } from "@/components/editor/ExportMenu";
import { parseSlides, type Slide } from "@/lib/slides";
import { SlideOverview } from "@/components/editor/SlideOverview";
import { SlidePresenter } from "@/components/editor/SlidePresenter";
import { SermonMetadataDialog } from "@/components/editor/SermonMetadata";
import { ResearchPane, type ResearchTab } from "@/components/editor/ResearchPane";
import { FootnoteList } from "@/components/editor/FootnoteList";
import {
  MarkdownEditor,
  type MarkdownEditorHandle,
} from "@/components/editor/MarkdownEditor";
import {
  collectFootnotes,
  inlineFootnoteMarkdown,
  type FootnoteEntry,
} from "@/components/editor/extensions/footnote-markdown";
import { countWords, minutesToReadableTime, extractHeadingsFromMarkdown } from "@/lib/editor";
import { useDocument } from "@/hooks/use-document";
import { useTheme } from "@/contexts/ThemeContext";
import { Quote as QuoteType } from "@/types";
import { get, post } from "@/lib/api";
import { APP_EVENTS } from "@/lib/brand";
import { blockquote } from "@/lib/markdown";
import { clearLocalDraft, readLocalDraft, saveLocalDraft } from "@/lib/draft-store";
import { useReconnect } from "@/hooks/use-reconnect";
import { useDesktopDocument } from "@/hooks/use-desktop-document";
import { runDocumentExport } from "@/lib/document-export";
import { isDesktopApp } from "@/lib/runtime";
import { FindBar } from "@/components/editor/FindBar";
import { VersionHistory } from "@/components/editor/VersionHistory";

type SaveStatus = "idle" | "dirty" | "saving" | "saved" | "failed" | "conflict";

/**
 * Would this write throw away work?
 *
 * Saving is a whole-document replace, so an empty buffer is a valid-looking
 * request that destroys a sermon. That is not hypothetical: a dev-server reload
 * remounted this page, the editor came up empty, an autosave fired before the
 * document had loaded into it, and 1,578 words went to the server as `""` —
 * after which `clearLocalDraft` deleted the only other copy because the write
 * had "succeeded".
 *
 * The asymmetry is the whole argument. Refusing a legitimate clear costs one
 * puzzled moment; allowing an illegitimate one costs a week's work.
 */
function isDestructiveWrite(next: string, previous: string | null): boolean {
  if (previous == null) return false;
  const before = previous.trim().length;
  if (before < 200) return false; // nothing here worth protecting
  const after = next.trim().length;
  return after === 0 || after < before * 0.1;
}

/**
 * Backoff for failed saves.
 *
 * The point is that a transient blip never reaches the user. Previously one
 * failed request showed "Save failed" and then nothing retried unless you
 * happened to keep typing — so a network hiccup while you paused looked like
 * data loss. These retries are silent; the status only turns to "failed" once
 * the whole ladder is exhausted.
 */
const RETRY_DELAYS_MS = [2_000, 5_000, 15_000];

function debounce<T extends (...args: any[]) => void>(fn: T, ms = 300) {
  let timeoutId: ReturnType<typeof setTimeout>;
  const wrapped = (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  };
  wrapped.cancel = () => clearTimeout(timeoutId);
  return wrapped;
}

/** Trailing debounce that also fires during uninterrupted typing. */
function debounceWithMaxWait<T extends (...args: any[]) => void>(
  fn: T,
  wait: number,
  maxWait: number,
) {
  let trailing: ReturnType<typeof setTimeout> | undefined;
  let maximum: ReturnType<typeof setTimeout> | undefined;
  const invoke = () => {
    clearTimeout(trailing);
    clearTimeout(maximum);
    trailing = undefined;
    maximum = undefined;
    fn();
  };
  const wrapped = () => {
    clearTimeout(trailing);
    trailing = setTimeout(invoke, wait);
    if (!maximum) maximum = setTimeout(invoke, maxWait);
  };
  wrapped.cancel = () => {
    clearTimeout(trailing);
    clearTimeout(maximum);
    trailing = undefined;
    maximum = undefined;
  };
  wrapped.flush = () => {
    if (trailing || maximum) invoke();
  };
  return wrapped;
}

export default function EditDocumentPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { mode, toggleMode, slideStyle } = useTheme();
  const [focusMode, setFocusMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [documentSidebarOpen, setDocumentSidebarOpen] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);
  const [researchTab, setResearchTab] = useState<ResearchTab>("scripture");
  const [sermonMetadataOpen, setSermonMetadataOpen] = useState(false);
  const [docType, setDocType] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState<string | null>(null);
  const [sessionStats, setSessionStats] = useState({
    duration: "0 mins",
    wordsWritten: 0,
    wordsPerMinute: 0,
    speakingTime: "0 mins",
  });
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [findOpen, setFindOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  /**
   * True once this document's text has actually been put into the editor.
   *
   * The difference between "the author emptied the document" and "the editor
   * has not been filled in yet" — which is exactly the distinction the save
   * guard below needs, and the one a remount destroys.
   */
  const documentLoadedRef = useRef(false);
  const [startTime] = useState(new Date());
  const [initialWordCount, setInitialWordCount] = useState(0);
  const [displayedWordCount, setDisplayedWordCount] = useState(0);
  const [headings, setHeadings] = useState<{ text: string; level: number }[]>([]);
  const [footnotes, setFootnotes] = useState<FootnoteEntry[]>([]);
  /** Zero until the document has a slide block, which is what reveals the UI. */
  const [slideCount, setSlideCount] = useState(0);
  /**
   * Markdown snapshot the slide overview is showing, or null when it is shut.
   * A snapshot rather than a live read: this page deliberately does not
   * re-render while someone is typing, so the overview is given the document as
   * it stood the moment it was opened.
   */
  const [slideOverviewSource, setSlideOverviewSource] = useState<string | null>(null);

  /**
   * The deck being presented, or null. A snapshot for the same reason the
   * overview takes one: the document must not change under a deck that is on a
   * screen in front of a congregation.
   */
  const [presenting, setPresenting] = useState<{ slides: Slide[]; index: number } | null>(
    null,
  );
  /**
   * Whether the presenter was started from the overview.
   *
   * Presenting and the light table are separate dialogs rather than nested
   * ones — nesting two full-screen layers only to unwind them in the right
   * order is more machinery than this needs. So the overview closes on the way
   * in, and finishing puts it back exactly where it was.
   */
  const returnToOverview = useRef(false);

  const openSlideOverview = useCallback(() => {
    setSlideOverviewSource(editorRef.current?.getMarkdown() ?? "");
  }, []);

  /** Close the overview and put the caret on the chosen slide. */
  const jumpToSlide = useCallback((slide: Slide) => {
    setSlideOverviewSource(null);
    const ed = editorRef.current;
    if (!ed) return;
    ed.select(slide.start, slide.end, { reveal: true });
    ed.focus();
  }, []);

  /** Start presenting from `index`, taking the document as it stands now. */
  const startPresenting = useCallback((index: number, fromOverview: boolean) => {
    const source = editorRef.current?.getMarkdown() ?? "";
    const { slides } = parseSlides(source);
    if (!slides.length) return;

    returnToOverview.current = fromOverview;
    if (fromOverview) setSlideOverviewSource(null);
    setPresenting({ slides, index: Math.max(0, Math.min(index, slides.length - 1)) });
  }, []);

  const stopPresenting = useCallback(() => {
    setPresenting(null);
    if (returnToOverview.current) {
      returnToOverview.current = false;
      setSlideOverviewSource(editorRef.current?.getMarkdown() ?? "");
    }
  }, []);

  const [isWritingProgrammatically, setIsWritingProgrammatically] = useState(false);
  /** Unsaved local content found on load, pending the author's decision. */
  const [recoverable, setRecoverable] = useState<{
    content: string;
    title?: string;
  } | null>(null);
  /** True only once a save has been in flight long enough to be worth showing. */
  const [savingVisible, setSavingVisible] = useState(false);
  const reconnectKey = useReconnect();
  /**
   * Brief acknowledgement for an explicit ⌘S.
   *
   * The indicator already reads "Saved" at rest, so a manual save would
   * otherwise change nothing on screen and feel unresponsive to someone
   * reaching for the shortcut out of habit. This is the smallest reply that
   * still counts as one.
   */
  const [manualSaveAck, setManualSaveAck] = useState(false);

  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastSavedContentRef = useRef("");
  const lastSavedTitleRef = useRef("");
  const currentTitleRef = useRef("");
  const pendingContentRef = useRef<string | null>(null);
  const isDirtyRef = useRef(false);
  /** Every title/body edit advances this. Successful writes may only clear
   * dirty state when they contain the newest revision. */
  const localRevisionRef = useRef(0);
  const syncedRevisionRef = useRef(0);
  const documentRef = useRef<import("@/types").Document | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const retryAttemptRef = useRef(0);
  const recoveryCheckedRef = useRef(false);
  /** Hashtag set last sent to the server, so we only sync on change. */
  const lastHashtagsRef = useRef<string | null>(null);
  /** True while code (not the keyboard) is writing — see runProgrammaticWrite. */
  const isProgrammaticWriteRef = useRef(false);
  /**
   * The editor's imperative handle.
   *
   * Held in state as well as a ref because the menus need it as a prop, and in
   * a ref because the debounced save callbacks are defined before the editor
   * mounts and must reach whatever the newest instance is when they fire.
   */
  const [editor, setEditor] = useState<MarkdownEditorHandle | null>(null);
  const editorRef = useRef<MarkdownEditorHandle | null>(null);

  const {
    document,
    sermonMetadata,
    isLoading,
    error,
    updateDocument,
    updateSermonMetadata,
  } = useDocument(id);

  useEffect(() => {
    documentRef.current = document;
  }, [document]);

  const openResearch = useCallback((tab: ResearchTab) => {
    setDocumentSidebarOpen(false);
    setResearchTab(tab);
    setResearchOpen(true);
  }, []);

  const closeResearch = useCallback(() => {
    setResearchOpen(false);
  }, []);

  const persistDocumentRef = useRef<
    (
      updates: Record<string, unknown>,
      options?: { silent?: boolean; revision?: number },
    ) => Promise<void>
  >(async () => undefined);

  persistDocumentRef.current = async (
    updates: Record<string, unknown>,
    options?: { silent?: boolean; revision?: number },
  ) => {
    const doc = documentRef.current;
    if (!doc) return;
    const revision = options?.revision ?? localRevisionRef.current;

    // Refuse to wipe a document that was never loaded into the editor.
    //
    // Checked before the local mirror is written, so a refused write leaves
    // both copies intact — mirroring first would overwrite the backup with the
    // very emptiness we are rejecting.
    if (
      typeof updates.content === "string" &&
      !documentLoadedRef.current &&
      isDestructiveWrite(updates.content, lastSavedContentRef.current)
    ) {
      console.warn(
        `[editor] Refused to save an empty buffer over document ${doc.id}: ` +
          "the document has not finished loading. Nothing was sent or mirrored.",
      );
      setSaveStatus("dirty");
      return;
    }

    // Mirror locally *before* the request. If this save fails, or the tab
    // closes mid-flight, the text still exists somewhere other than React
    // state. Fire-and-forget so it never delays the write.
    if (typeof updates.content === "string") {
      saveLocalDraft({
        documentId: doc.id,
        content: updates.content,
        title:
          typeof updates.title === "string" ? updates.title : currentTitleRef.current,
        revision,
        baseVersion: doc.version ?? null,
        savedAt: Date.now(),
      });
    }

    if (!options?.silent) setSaveStatus("saving");
    try {
      await updateDocument(updates as any);
      clearTimeout(retryTimeoutRef.current);
      retryAttemptRef.current = 0;

      if (typeof updates.content === "string") {
        lastSavedContentRef.current = updates.content;
        if (revision === localRevisionRef.current) pendingContentRef.current = null;
        // Local version history (Mac only). Recorded after the server accepted
        // the write, so the timeline never contains a state the author lost to
        // a conflict. Throttling lives in desktop-history.
        if (isDesktopApp) {
          const content = updates.content;
          void import("@/lib/desktop-history")
            .then(({ recordSnapshot }) =>
              recordSnapshot({
                documentId: doc.id,
                title:
                  typeof updates.title === "string"
                    ? updates.title
                    : currentTitleRef.current,
                content,
                version: documentRef.current?.version ?? null,
                wordCount:
                  typeof updates.word_count === "number"
                    ? updates.word_count
                    : countWords(content),
              }),
            )
            .catch(() => undefined);
        }
        // Link #hashtags found in content (additive; never auto-detach).
        const hashtags = [
          ...new Set(
            [...updates.content.matchAll(/#([A-Za-z][\w-]*)/g)].map((m) =>
              m[1].toLowerCase(),
            ),
          ),
        ]
          .sort()
          .join(",");
        if (hashtags && hashtags !== lastHashtagsRef.current) {
          lastHashtagsRef.current = hashtags;
          post(`/documents/${doc.id}/tags/sync-hashtags`, {
            content: updates.content,
          }).catch(() => undefined);
        }
      }
      if (typeof updates.title === "string") {
        lastSavedTitleRef.current = updates.title;
      }
      syncedRevisionRef.current = Math.max(syncedRevisionRef.current, revision);
      const caughtUp = revision === localRevisionRef.current;
      if (caughtUp) {
        isDirtyRef.current = false;
        // The mirror is deliberately *not* cleared here any more. It used to be,
        // on the reasoning that the server now had the text — which is true right
        // up until something overwrites the server copy, at which point the only
        // other copy has just been deleted. Keeping it costs one small record per
        // document; the recovery check below stays quiet whenever it agrees with
        // what loaded.
      }
      if (!options?.silent) {
        setSaveStatus(caughtUp ? "saved" : "dirty");
        setTimeout(() => setSaveStatus((s) => (s === "saved" ? "idle" : s)), 2000);
      }
    } catch (err) {
      const conflicted = (err as { status?: number })?.status === 409;
      if (conflicted) {
        clearTimeout(retryTimeoutRef.current);
        retryAttemptRef.current = 0;
        setSaveStatus("conflict");
        throw err;
      }

      // Transient. Retry quietly; the author shouldn't feel a blip.
      const attempt = retryAttemptRef.current;
      if (attempt < RETRY_DELAYS_MS.length) {
        retryAttemptRef.current = attempt + 1;
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = setTimeout(() => {
          void flushLatestRef.current().catch(() => undefined);
        }, RETRY_DELAYS_MS[attempt]);
        if (!options?.silent) setSaveStatus("dirty");
      } else {
        console.error(err);
        setSaveStatus("failed");
      }
      throw err;
    }
  };

  const persistDocument = useCallback(
    (
      updates: Record<string, unknown>,
      options?: { silent?: boolean; revision?: number },
    ) => persistDocumentRef.current(updates, options),
    [],
  );

  /** Breaks the persistDocument/flushPendingSave callback cycle while letting
   * retry timers always ask for the newest editor snapshot. */
  const flushLatestRef = useRef<() => Promise<void>>(async () => undefined);

  const flushPendingSave = useCallback(async () => {
    if (!documentRef.current) return;
    const editorInstance = editorRef.current;
    const content = editorInstance
      ? editorInstance.getMarkdown()
      : pendingContentRef.current;

    const titleChanged = currentTitleRef.current !== lastSavedTitleRef.current;
    const contentChanged = content != null && content !== lastSavedContentRef.current;

    if (!titleChanged && !contentChanged && !isDirtyRef.current) return;

    const targetContent = content ?? lastSavedContentRef.current ?? "";
    const payload: Record<string, unknown> = {
      content: targetContent,
      title: currentTitleRef.current,
      word_count: countWords(targetContent),
    };

    await persistDocumentRef.current(payload, { revision: localRevisionRef.current });
  }, []);

  useEffect(() => {
    flushLatestRef.current = flushPendingSave;
  }, [flushPendingSave]);

  const scheduleContentSaveRef = useRef<ReturnType<typeof debounceWithMaxWait> | null>(null);
  if (!scheduleContentSaveRef.current) {
    scheduleContentSaveRef.current = debounceWithMaxWait(() => {
      const ed = editorRef.current;
      if (!ed) return;
      const content = ed.getMarkdown();
      if (
        content === lastSavedContentRef.current &&
        currentTitleRef.current === lastSavedTitleRef.current
      ) return;
      pendingContentRef.current = content;
      void persistDocumentRef
        .current(
          {
            content,
            title: currentTitleRef.current,
            word_count: countWords(content),
          },
          { revision: localRevisionRef.current },
        )
        .catch(() => undefined);
    }, 900, 4000);
  }
  const scheduleContentSave = scheduleContentSaveRef.current;

  const scheduleLocalCheckpointRef = useRef<ReturnType<typeof debounceWithMaxWait> | null>(null);
  if (!scheduleLocalCheckpointRef.current) {
    scheduleLocalCheckpointRef.current = debounceWithMaxWait(() => {
      const ed = editorRef.current;
      const doc = documentRef.current;
      if (!ed || !doc) return;
      const content = ed.getMarkdown();
      pendingContentRef.current = content;
      saveLocalDraft({
        documentId: doc.id,
        title: currentTitleRef.current,
        content,
        revision: localRevisionRef.current,
        baseVersion: doc.version ?? null,
        savedAt: Date.now(),
      });
    }, 150, 1000);
  }
  const scheduleLocalCheckpoint = scheduleLocalCheckpointRef.current;

  /**
   * Word count and outline headings — display only, so they can lag typing.
   * Recomputing them per keystroke was two more full passes over the document
   * for numbers nobody reads mid-word.
   */
  const scheduleStats = useCallback(
    debounce(() => {
      const ed = editorRef.current;
      if (!ed) return;
      const markdown = ed.getMarkdown();
      setDisplayedWordCount(countWords(markdown));
      setHeadings(extractHeadingsFromMarkdown(markdown));
      // Scans the whole document, so it rides the same debounce rather than the
      // typing path — see the note in FootnoteList.
      setFootnotes(collectFootnotes(markdown));
      setSlideCount(parseSlides(markdown).slides.length);
    }, 500),
    [],
  );

  /**
   * Every edit, typed or programmatic.
   *
   * This runs on every keystroke, so it must stay O(1). Counting words,
   * rescanning headings and collecting footnotes here would be three full
   * passes over the document per character — which is what makes fast typing
   * stutter. All of it is deferred to the debounced callbacks above, which read
   * the editor when they fire.
   *
   * The content itself is not captured here either. The textarea is the buffer,
   * so `getMarkdown()` is always current and every consumer reads it on demand
   * rather than being handed a copy per character.
   */
  const handleEditorChange = useCallback(() => {
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);

    isDirtyRef.current = true;
    if (isProgrammaticWriteRef.current) return;

    localRevisionRef.current += 1;
    clearTimeout(retryTimeoutRef.current);
    retryAttemptRef.current = 0;

    // React bails out when the value is unchanged, so this is free after the
    // first keystroke of a burst.
    setSaveStatus("dirty");
    scheduleLocalCheckpoint();
    scheduleContentSave();
    scheduleStats();
  }, [scheduleContentSave, scheduleLocalCheckpoint, scheduleStats]);

  // Keep the editor reachable from callbacks defined before it mounts.
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  /**
   * Push any stranded work when the connection comes back.
   *
   * The retry ladder is finite (2s, 5s, 15s) and only resets on success, so a
   * save that failed while offline would land on "Couldn't save" and then
   * never try again unless the author happened to type another character. The
   * text was safe in the local mirror, but the server never got it while the
   * app looked perfectly healthy.
   *
   * A conflict is excluded on purpose — retrying that would overwrite whoever
   * actually changed the document, which is the thing the conflict check
   * exists to prevent.
   */
  const reconnectSeenRef = useRef(reconnectKey);
  useEffect(() => {
    if (reconnectSeenRef.current === reconnectKey) return;
    reconnectSeenRef.current = reconnectKey;

    if (saveStatus === "conflict") return;
    retryAttemptRef.current = 0;
    clearTimeout(retryTimeoutRef.current);
    retryAttemptRef.current = 0;

    if (isDirtyRef.current || pendingContentRef.current != null) {
      void flushPendingSave().catch(() => undefined);
    }
    // saveStatus is read, not tracked — this must run on reconnect only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectKey, flushPendingSave]);

  // A save finishing inside this window never announces itself.
  useEffect(() => {
    if (saveStatus !== "saving") {
      setSavingVisible(false);
      return;
    }
    const id = setTimeout(() => setSavingVisible(true), 800);
    return () => clearTimeout(id);
  }, [saveStatus]);

  /**
   * Recover a local draft that never reached the server.
   *
   * Only offered when the mirror genuinely differs from what loaded — after a
   * clean save the mirror is cleared, so in normal use this never fires and the
   * author sees nothing. It exists for the crash, the closed tab, and the
   * hours-long outage.
   */
  useEffect(() => {
    if (!editor || !document || recoveryCheckedRef.current) return;
    recoveryCheckedRef.current = true;

    void (async () => {
      const draft = await readLocalDraft(document.id);
      if (!draft) return;
      // Normalised the same way the editor loads it, so a reference-style
      // document doesn't look like a divergent draft purely because the
      // buffer holds the converted form.
      const server = inlineFootnoteMarkdown(document.content || "");
      const sameTitle = draft.title == null || draft.title === document.title;
      // Agreeing with the server is the normal case and means there is nothing
      // to offer — but the mirror is kept, so it is still there if the server
      // copy is later lost.
      if (draft.content.trim() === server.trim() && sameTitle) return;
      setRecoverable({ content: draft.content, title: draft.title });
    })();
  }, [editor, document]);

  /**
   * Load the document into the editor — once, when it first arrives.
   *
   * This used to re-run whenever `document` changed, which is on *every save*,
   * because saving calls setDocument with the server's row. The only thing
   * stopping it overwriting the buffer was a 100ms setTimeout guard, and that
   * race was most likely to be lost exactly when React was busy — i.e. while
   * typing fast. Losing it meant the word count snapped back to the saved
   * value and the editor could be re-set from stale content mid-sentence.
   *
   * Keying on the document id removes the feedback loop rather than racing it:
   * once loaded, nothing the save path does can touch what you're typing.
   */
  const loadedDocIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!editor || !document) return;
    if (loadedDocIdRef.current === document.id) return;
    loadedDocIdRef.current = document.id;

    // Accept reference-style footnotes on the way in — anything pasted from
    // Obsidian, pandoc or a static site generator uses them. `lastSavedContent`
    // is set to the converted text below, so simply opening such a document
    // doesn't rewrite it; the new form is only persisted once the author edits.
    const incoming = inlineFootnoteMarkdown(document.content || "");
    // Not an edit: this is the starting state, and undo must not reach past it.
    editor.setMarkdown(incoming);
    // From here an empty buffer means the author emptied it, so the save guard
    // stands down.
    documentLoadedRef.current = true;

    lastSavedContentRef.current = incoming;
    lastSavedTitleRef.current = document.title;
    currentTitleRef.current = document.title;
    setDraftTitle(document.title);
    const words = document.word_count || countWords(incoming);
    setInitialWordCount(words);
    setDisplayedWordCount(words);
    setHeadings(extractHeadingsFromMarkdown(incoming));
    setFootnotes(collectFootnotes(incoming));
    setSlideCount(parseSlides(incoming).slides.length);

    // Pick up where the author left off — the caret position this document
    // was closed at, if we have one, otherwise the end. Placed after the
    // value has been committed to the DOM, hence the frame's delay.
    const savedCaret = Number(
      localStorage.getItem(`sermonspace:caret:${document.id}`) ?? Number.NaN,
    );
    const caret =
      Number.isFinite(savedCaret) && savedCaret >= 0
        ? Math.min(savedCaret, incoming.length)
        : incoming.length;
    requestAnimationFrame(() =>
      editor.select(caret, caret, { reveal: caret < incoming.length }),
    );
  }, [editor, document]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      // Remember the caret so reopening this document resumes mid-thought.
      const doc = documentRef.current;
      const selection = editorRef.current?.getSelection();
      if (doc && selection) {
        localStorage.setItem(`sermonspace:caret:${doc.id}`, String(selection.start));
      }
      scheduleContentSave.cancel?.();
      // Local durability does not depend on a network request surviving
      // navigation. Force the pending checkpoint before TipTap is destroyed.
      scheduleLocalCheckpoint.flush?.();
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
      clearTimeout(retryTimeoutRef.current);
      // fire-and-forget flush
      void flushPendingSave();
    };
  }, [flushPendingSave, scheduleContentSave, scheduleLocalCheckpoint]);

  // beforeunload when dirty
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current || pendingContentRef.current != null) {
        scheduleLocalCheckpoint.flush?.();
        e.preventDefault();
        e.returnValue = "";
        void flushPendingSave();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [flushPendingSave, scheduleLocalCheckpoint]);

  // Flush on tab blur
  useEffect(() => {
    const onVisibility = () => {
      if (window.document.visibilityState === "hidden") {
        scheduleLocalCheckpoint.flush?.();
        void flushPendingSave();
      }
    };
    window.document.addEventListener("visibilitychange", onVisibility);
    return () => window.document.removeEventListener("visibilitychange", onVisibility);
  }, [flushPendingSave, scheduleLocalCheckpoint]);

  // Depends on the type id, NOT the document object. `document` gets a new
  // identity on every save, so keying this on it refetched a static lookup
  // table once per save — a full round-trip, forever, for data that never
  // changes.
  const docTypeId = document?.doc_type_id;
  useEffect(() => {
    if (docTypeId == null) return;
    let cancelled = false;
    void (async () => {
      try {
        const types = await get<{ id: number; name: string }[]>("/document-types");
        if (cancelled) return;
        setDocType(types.find((t) => t.id === docTypeId)?.name || null);
      } catch (err) {
        console.error("Error fetching document type:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docTypeId]);

  // Focus mode body class + close rails
  useEffect(() => {
    const body = window.document.body;
    if (focusMode) {
      body.classList.add("focus-mode");
      closeResearch(); setDocumentSidebarOpen(false);
      setSidebarOpen(false);
    } else {
      body.classList.remove("focus-mode");
    }
    return () => body.classList.remove("focus-mode");
  }, [focusMode, closeResearch]);

  // Session stats
  useEffect(() => {
    const tick = () => {
      const diffInMinutes = (Date.now() - startTime.getTime()) / (1000 * 60);
      const wordsWritten = Math.max(0, displayedWordCount - initialWordCount);
      const wpm = diffInMinutes > 0 ? Math.round(wordsWritten / diffInMinutes) : 0;
      const speakingMins = displayedWordCount / 130;
      setSessionStats({
        duration: minutesToReadableTime(diffInMinutes),
        wordsWritten,
        wordsPerMinute: wpm > 0 ? wpm : 0,
        speakingTime: minutesToReadableTime(speakingMins),
      });
    };
    tick();
    const interval = setInterval(tick, 15000);
    return () => clearInterval(interval);
  }, [startTime, displayedWordCount, initialWordCount]);

  const handleTitleChange = useCallback((title: string) => {
    if (!documentRef.current) return;
    currentTitleRef.current = title;
    setDraftTitle(title);
    localRevisionRef.current += 1;
    isDirtyRef.current = true;
    clearTimeout(retryTimeoutRef.current);
    setSaveStatus("dirty");
    scheduleLocalCheckpoint();
    scheduleContentSave();
  }, [scheduleContentSave, scheduleLocalCheckpoint]);

  const handleSave = useCallback(async () => {
    if (!documentRef.current || !editor) return;
    // A programmatic write saves itself on completion; saving a half-streamed
    // buffer here would race that final write.
    if (isProgrammaticWriteRef.current) return;
    scheduleContentSave.cancel?.();
    const content = editor.getMarkdown();
    pendingContentRef.current = content;
    try {
      await persistDocument({
        content,
        title: currentTitleRef.current,
        word_count: countWords(content),
      }, { revision: localRevisionRef.current });
      setManualSaveAck(true);
      setTimeout(() => setManualSaveAck(false), 1800);
    } catch {
      /* status failed */
    }
  }, [editor, persistDocument, scheduleContentSave]);

  useDesktopDocument(
    document
      ? {
          id: document.id,
          title: draftTitle ?? document.title,
          dirty:
            saveStatus === "dirty" ||
            saveStatus === "failed" ||
            saveStatus === "saving" ||
            saveStatus === "conflict",
          hasSlides: slideCount > 0,
          save: () => void handleSave(),
          print: () => {
            const content = editorRef.current?.getMarkdown() ?? document.content ?? "";
            void runDocumentExport({
              kind: "manuscript-print",
              title: draftTitle ?? document.title,
              content,
              slideStyle,
            });
          },
          close: () => navigate("/"),
          getContent: () => editorRef.current?.getMarkdown() ?? document.content ?? "",
          exportKind: (kind) => {
            const content = editorRef.current?.getMarkdown() ?? document.content ?? "";
            void runDocumentExport({
              kind,
              title: draftTitle ?? document.title,
              content,
              slideStyle,
            });
          },
        }
      : null,
  );

  useEffect(() => {
    const onConflict = (event: Event) => {
      const id = (event as CustomEvent<{ documentId?: number }>).detail?.documentId;
      if (id != null && documentRef.current && id !== documentRef.current.id) return;
      setSaveStatus("conflict");
    };
    window.addEventListener(APP_EVENTS.syncConflict, onConflict);
    return () => window.removeEventListener(APP_EVENTS.syncConflict, onConflict);
  }, []);

  /**
   * Run a write that originates from code rather than the keyboard — streamed
   * model output, or any bulk insertion.
   *
   * The autosave machine assumes a single writer. Every transaction
   * re-serializes the document, rescans it for word count and headings, and
   * re-arms the debounced save; the document-sync effect separately assumes
   * the editor only diverges from `document` because someone typed. Both hold
   * at typing speed and neither holds at token speed. Bracketing a write in
   * here suppresses the per-transaction work and the sync effect, then
   * recomputes and persists exactly once at the end.
   *
   * The callback receives `insert` (append at the cursor, for streaming) and
   * `replaceAll` (swap the whole document). Rejections propagate to the caller
   * after whatever was already written has been saved — a stream that dies
   * halfway keeps its partial output rather than discarding it.
   */
  const runProgrammaticWrite = useCallback(
    async <T,>(
      write: (ops: {
        insert: (text: string) => void;
        replaceAll: (markdown: string) => void;
      }) => Promise<T> | T,
    ): Promise<T> => {
      const ed = editor;
      if (!ed) throw new Error("Editor is not ready");
      if (isProgrammaticWriteRef.current) {
        throw new Error("A programmatic write is already in progress");
      }

      // Cancel — don't flush — any debounced save the user's typing armed.
      // The final save below reads the whole buffer, so it captures their
      // keystrokes and this write together; flushing first would put a
      // network round-trip in front of the insert appearing on screen.
      scheduleContentSave.cancel?.();

      isProgrammaticWriteRef.current = true;
      localRevisionRef.current += 1;
      clearTimeout(retryTimeoutRef.current);
      retryAttemptRef.current = 0;
      setIsWritingProgrammatically(true);
      setSaveStatus("dirty");
      // Both write paths go through the browser's own editing commands, which
      // require the textarea to hold focus — and which is what puts these
      // writes on the undo stack alongside the author's typing.
      ed.focus();

      try {
        return await write({
          // No per-chunk focus: the insertion advances the caret on its own,
          // and re-focusing every token fights the user's scroll.
          insert: (text: string) => ed.insert(text),
          replaceAll: (markdown: string) => {
            ed.replaceAll(markdown);
            isDirtyRef.current = true;
          },
        });
      } finally {
        isProgrammaticWriteRef.current = false;
        setIsWritingProgrammatically(false);

        const markdown = ed.getMarkdown();
        const wordCount = countWords(markdown);
        const doc = documentRef.current;
        setDisplayedWordCount(wordCount);
        setHeadings(extractHeadingsFromMarkdown(markdown));
        setFootnotes(collectFootnotes(markdown));
        setSlideCount(parseSlides(markdown).slides.length);

        if (doc) {
          saveLocalDraft({
            documentId: doc.id,
            title: currentTitleRef.current,
            content: markdown,
            revision: localRevisionRef.current,
            baseVersion: doc.version ?? null,
            savedAt: Date.now(),
          });
        }

        if (markdown !== lastSavedContentRef.current) {
          isDirtyRef.current = true;
          pendingContentRef.current = markdown;
          void persistDocument(
            {
              content: markdown,
              title: currentTitleRef.current,
              word_count: wordCount,
            },
            { revision: localRevisionRef.current },
          ).catch(() => undefined);
        } else {
          setSaveStatus("idle");
        }
      }
    },
    [editor, persistDocument, scheduleContentSave],
  );

  /** Bring back a past version — undoable, like any other programmatic write. */
  const handleRestoreVersion = useCallback(
    (snapshot: { content: string; title: string }) => {
      if (snapshot.title && snapshot.title !== currentTitleRef.current) {
        handleTitleChange(snapshot.title);
      }
      void runProgrammaticWrite(({ replaceAll }) => replaceAll(snapshot.content)).catch(
        () => undefined,
      );
    },
    [handleTitleChange, runProgrammaticWrite],
  );

  const setFocus = useCallback((on: boolean) => {
    setFocusMode(on);
    if (on) {
      setResearchOpen(false);
      setDocumentSidebarOpen(false);
    }
  }, []);

  // These are all code-originated writes, so they go through the same path as
  // streamed output: bookkeeping suppressed during the insert, then one
  // recompute and one save. Previously they relied on the typing path, which
  // meant an inserted quote sat unsaved until the 1.2s debounce elapsed.
  const handleQuoteInsert = (quote: QuoteType) => {
    const quoteText = `${blockquote(quote.content)}\n>\n> — ${quote.author}${quote.source ? `, ${quote.source}` : ""}\n\n`;
    void runProgrammaticWrite(({ insert }) => insert(quoteText)).catch(() => undefined);
  };

  const handleScriptureInsert = (payload: {
    reference: string;
    text: string;
  }) => {
    const block = `> **${payload.reference}**\n>\n${blockquote(payload.text)}\n\n`;
    void runProgrammaticWrite(({ insert }) => insert(block)).catch(() => undefined);
  };

  const handleNoteInsert = (content: string) => {
    void runProgrammaticWrite(({ insert }) => insert(`${content}\n\n`)).catch(() => undefined);
  };

  /**
   * Take the cursor to a footnote and select it.
   *
   * Offsets come from the list, which is recomputed on a 500ms debounce, so
   * they can be stale by a few characters after a fast edit. `select` clamps to
   * the document rather than throwing, so a stale offset lands nearby instead
   * of failing.
   */
  const jumpToFootnote = (note: FootnoteEntry) => {
    editor?.select(note.pos, note.end);
  };

  /**
   * Take the cursor to a heading.
   *
   * Located by rescanning the source rather than from a stored offset: the
   * outline is recomputed on a debounce, so any offset captured with it would
   * be stale by exactly the amount someone typed since. The scan is over a
   * string that is already in memory, and only runs when someone clicks.
   */
  const jumpToHeading = (headingText: string) => {
    const ed = editorRef.current;
    if (!ed) return;

    const markdown = ed.getMarkdown();
    let offset = 0;
    for (const line of markdown.split("\n")) {
      const match = /^(\s*)(#{1,6})[ \t]+(.*)$/.exec(line);
      if (match && match[3].trim() === headingText) {
        const at = offset + match[1].length + match[2].length + 1;
        ed.select(at, offset + line.length);
        return;
      }
      offset += line.length + 1;
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;

      if (e.key === "Escape") {
        if (findOpen) {
          e.preventDefault();
          setFindOpen(false);
          editorRef.current?.focus();
          return;
        }
        if (sidebarOpen) {
          e.preventDefault();
          setSidebarOpen(false);
          return;
        }
        if (researchOpen) {
          e.preventDefault();
          closeResearch();
          return;
        }
        if (documentSidebarOpen) {
          e.preventDefault();
          setDocumentSidebarOpen(false);
          return;
        }
        if (focusMode) {
          e.preventDefault();
          setFocus(false);
          return;
        }
      }

      if (mod && !e.shiftKey && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        void handleSave();
        return;
      }

      // In the Mac app the Edit ▸ Find menu item carries this accelerator and
      // dispatches openFind instead; this handler is the web's path in, and a
      // fallback if the key reaches the page anyway.
      if (mod && !e.shiftKey && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        setFindOpen(true);
        return;
      }

      if (mod && !e.shiftKey && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        const content = editorRef.current?.getMarkdown() ?? documentRef.current?.content ?? "";
        const title = currentTitleRef.current || "Untitled";
        void runDocumentExport({
          kind: "manuscript-print",
          title,
          content,
          slideStyle,
        });
        return;
      }

      if (mod && e.shiftKey) {
        switch (e.key.toLowerCase()) {
          case "d":
            e.preventDefault();
            setSidebarOpen((v) => !v);
            break;
          case "f":
            e.preventDefault();
            setFocus(!focusMode);
            break;
          case "n":
            e.preventDefault();
            if (researchOpen && researchTab === "notes") closeResearch();
            else openResearch("notes");
            break;
          case "b":
            e.preventDefault();
            if (researchOpen && researchTab === "scripture") closeResearch();
            else openResearch("scripture");
            break;
          case "q":
            e.preventDefault();
            if (researchOpen && researchTab === "quotes") closeResearch();
            else openResearch("quotes");
            break;
          case "t":
            e.preventDefault();
            toggleMode();
            break;
          case "o":
            e.preventDefault();
            if (researchOpen && researchTab === "outline") closeResearch();
            else openResearch("outline");
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    closeResearch,
    documentSidebarOpen,
    findOpen,
    focusMode,
    handleSave,
    openResearch,
    researchOpen,
    researchTab,
    sidebarOpen,
    setFocus,
    slideStyle,
    toggleMode,
  ]);

  // The Mac menu bar's Edit ▸ Find lands here.
  useEffect(() => {
    const onOpenFind = () => setFindOpen(true);
    window.addEventListener(APP_EVENTS.openFind, onOpenFind);
    return () => window.removeEventListener(APP_EVENTS.openFind, onOpenFind);
  }, []);

  /**
   * Deliberately passive wording. A Save button implied saving was the
   * author's job; this only reports state. "Saving…" is gated behind
   * `savingVisible`, so a save that completes quickly — nearly all of them —
   * produces no visible change at all, and "dirty" shows nothing rather than
   * flashing "Unsaved" between keystrokes.
   */
  const saveLabel = isWritingProgrammatically
    ? "Writing…"
    : saveStatus === "conflict"
      ? "Changed elsewhere"
      : saveStatus === "failed"
        ? "Saved on this device · sync paused"
        : saveStatus === "saving" && savingVisible
          ? "Syncing…"
          : saveStatus === "dirty"
            ? ""
            : manualSaveAck
              ? "Saved just now"
              : "Saved";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-t-2 border-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <FileText className="h-16 w-16 text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Document Not Found</h1>
        <p className="text-muted-foreground mb-6">
          {error || "Unable to load the document."}
        </p>
        <Link href="/">
          <Button>Return to Dashboard</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden">
      {/* Both banners are silent in normal use. The conflict one only appears
          when the server actually rejected a write; the recovery one only when
          a local mirror survived that the server never received. */}
      {saveStatus === "conflict" && (
        <div className="fixed top-0 inset-x-0 z-[70] flex flex-wrap items-center justify-center gap-2 bg-destructive px-4 py-2 text-sm text-destructive-foreground">
          <span>
            This document was edited somewhere else, so saving is paused rather
            than overwriting it. Your text here is backed up.
          </span>
          <Button
            size="sm"
            variant="secondary"
            className="h-6"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </div>
      )}

      {recoverable !== null && (
        <div className="fixed top-0 inset-x-0 z-[70] flex flex-wrap items-center justify-center gap-2 bg-amber-500/95 px-4 py-2 text-sm text-amber-950">
          <span>Unsaved changes from a previous session were found.</span>
          <Button
            size="sm"
            variant="secondary"
            className="h-6"
            onClick={() => {
              const draft = recoverable;
              setRecoverable(null);
              if (!editor || draft == null) return;
              if (draft.title) {
                currentTitleRef.current = draft.title;
                handleTitleChange(draft.title);
              }
              void runProgrammaticWrite(({ replaceAll }) => replaceAll(draft.content)).catch(
                () => undefined,
              );
            }}
          >
            Restore them
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 hover:bg-amber-600/30"
            onClick={() => {
              if (document) clearLocalDraft(document.id);
              setRecoverable(null);
            }}
          >
            Discard
          </Button>
        </div>
      )}

      {/* Never docked: the editor is a focus surface, so the library is
          summoned over it and dismissed, at every width. */}
      <Sidebar docked={false} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div
        className="flex h-screen flex-col"
      >
        <header className={`editor-chrome bg-card border-b border-border sticky top-0 z-20 ${!sidebarOpen ? "desktop-chrome-offset" : ""}`}>
          <div className="flex items-center justify-between h-14 px-4">
            <div className="flex items-center flex-1 min-w-0">
              {!sidebarOpen && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(true)}
                  className="mr-2 shrink-0"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              )}

              <DocumentTitle
                title={draftTitle ?? document.title}
                onChange={handleTitleChange}
              />

              <span
                className={`text-sm ml-4 shrink-0 flex items-center gap-1 ${
                  saveStatus === "failed" || saveStatus === "conflict"
                    ? "text-destructive"
                    : "text-muted-foreground"
                }`}
              >
                {saveStatus === "failed" && <AlertCircle className="h-3.5 w-3.5" />}
                {saveLabel}
                {saveStatus === "failed" && (
                  <button
                    type="button"
                    className="underline ml-1"
                    onClick={() => void handleSave()}
                  >
                    Try again
                  </button>
                )}
              </span>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <div className="hidden lg:flex items-center gap-3 text-xs text-muted-foreground mr-1">
                <span>{displayedWordCount} words</span>
                <span className="text-border">·</span>
                <span>~{sessionStats.speakingTime} speak</span>
                {/* Absent entirely until the document has a slide, so an
                    ordinary sermon never carries chrome for a feature it is
                    not using. */}
                {slideCount > 0 && (
                  <>
                    <span className="text-border">·</span>
                    <button
                      type="button"
                      onClick={openSlideOverview}
                      className="hover:text-foreground underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
                      title="Preview the slides"
                    >
                      {slideCount} {slideCount === 1 ? "slide" : "slides"}
                    </button>
                  </>
                )}
                {sessionStats.wordsWritten > 0 && (
                  <>
                    <span className="text-border">·</span>
                    <span>+{sessionStats.wordsWritten} this session</span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant={focusMode ? "secondary" : "ghost"}
                  size="icon"
                  onClick={() => setFocus(!focusMode)}
                  title="Focus mode (Ctrl+Shift+F)"
                >
                  {focusMode ? (
                    <Minimize className="h-5 w-5" />
                  ) : (
                    <Maximize className="h-5 w-5" />
                  )}
                </Button>

                <Button
                  type="button"
                  variant={researchOpen ? "secondary" : "ghost"}
                  size="icon"
                  title="Research (Esc to close)"
                  onClick={() => {
                    if (researchOpen) closeResearch();
                    else openResearch(researchTab);
                  }}
                >
                  <BookText className="h-5 w-5" />
                </Button>


                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" title="More">
                      <MoreHorizontal className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={toggleMode}>
                      {mode === "dark" ? (
                        <Sun className="h-4 w-4 mr-2" />
                      ) : (
                        <Moon className="h-4 w-4 mr-2" />
                      )}
                      {mode === "dark" ? "Light mode" : "Dark mode"}
                    </DropdownMenuItem>
                    {docType === "Sermon" && (
                      <DropdownMenuItem onClick={() => setSermonMetadataOpen(true)}>
                        <BookOpen className="h-4 w-4 mr-2" />
                        Sermon metadata
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => {
                        closeResearch();
                        setDocumentSidebarOpen(true);
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Document details
                    </DropdownMenuItem>
                    {isDesktopApp && (
                      <DropdownMenuItem onClick={() => setHistoryOpen(true)}>
                        <History className="h-4 w-4 mr-2" />
                        Version history
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link href={`/documents/${document.id}`}>
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </Link>
                    </DropdownMenuItem>
                    {slideCount > 0 && (
                      <>
                        <DropdownMenuItem onClick={openSlideOverview}>
                          <Presentation className="h-4 w-4 mr-2" />
                          Preview slides
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => startPresenting(0, false)}>
                          <Play className="h-4 w-4 mr-2" />
                          Present slides
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <FileDown className="h-4 w-4 mr-2" />
                        Export
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <ExportMenu
                          itemsOnly
                          title={draftTitle ?? document.title}
                          content={() => editor?.getMarkdown() ?? document.content ?? ""}
                        />
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setSidebarOpen((v) => !v)}>
                      <Menu className="h-4 w-4 mr-2" />
                      {sidebarOpen ? "Hide library" : "Show library"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          <FindBar editor={editor} open={findOpen} onClose={() => setFindOpen(false)} />
        </header>

        <main
          className="flex-1 overflow-y-auto bg-background"
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              void flushPendingSave();
            }
          }}
        >
          <div
            className={`mx-auto px-6 py-10 transition-all ${
              focusMode ? "max-w-[42rem]" : "max-w-3xl"
            }`}
          >
            <MarkdownEditor
              ref={setEditor}
              className="editor-content text-foreground"
              placeholder="Begin writing… Type / for blocks"
              ariaLabel={draftTitle ?? document.title}
              onChange={handleEditorChange}
            />
            <EditorMenus editor={editor} />
            <SlideOverview
              source={slideOverviewSource}
              title={draftTitle ?? document.title}
              onClose={() => setSlideOverviewSource(null)}
              onJumpTo={jumpToSlide}
              onPresent={(index) => startPresenting(index, true)}
            />
            <SlidePresenter
              open={presenting !== null}
              slides={presenting?.slides ?? []}
              startIndex={presenting?.index ?? 0}
              onClose={stopPresenting}
              style={slideStyle}
            />
            <FootnoteList notes={footnotes} onSelect={jumpToFootnote} />
          </div>
        </main>

        {focusMode && (
          <button
            type="button"
            onClick={() => setFocus(false)}
            className="focus-exit-affordance fixed top-3 left-1/2 -translate-x-1/2 z-50 text-xs text-muted-foreground/60 hover:text-foreground bg-card/80 backdrop-blur px-3 py-1 rounded-full border border-border/50 opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity"
          >
            Exit focus · Esc
          </button>
        )}
      </div>

      <DocumentSidebar
        isOpen={documentSidebarOpen && !focusMode}
        onClose={() => setDocumentSidebarOpen(false)}
        document={document}
        sermonMetadata={sermonMetadata}
        onUpdateSermonMetadata={updateSermonMetadata}
      />

      <ResearchPane
        isOpen={researchOpen && !focusMode}
        onClose={closeResearch}
        tab={researchTab}
        onTabChange={setResearchTab}
        documentId={document.id}
        documentTitle={draftTitle ?? document.title}
        headings={headings}
        onInsertScripture={handleScriptureInsert}
        onInsertQuote={handleQuoteInsert}
        onInsertNote={handleNoteInsert}
        onJumpToHeading={jumpToHeading}
      />

      {isDesktopApp && (
        <VersionHistory
          documentId={document.id}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          onRestore={handleRestoreVersion}
        />
      )}

      {docType === "Sermon" && (
        <SermonMetadataDialog
          isOpen={sermonMetadataOpen}
          onClose={() => setSermonMetadataOpen(false)}
          metadata={
            sermonMetadata
              ? { ...sermonMetadata, document_id: document.id }
              : { document_id: document.id }
          }
          onSave={updateSermonMetadata}
        />
      )}
    </div>
  );
}
